import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Trash2, Send, Info, AlertTriangle, Lock, FileArchive, BookOpen } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { FactureBrouillonModal, EmettreRecapModal } from "@/components/FactureBrouillon";
import { api, fmtError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR, fmtDate, L } from "@/constants/labels";
import { MesCommissions } from "@/components/commission/MesCommissions";

const REGIMES = [
  { value: "autoliquidation", label: "Autoliquidation — bâtiment B2B (art. 283-2 nonies CGI)" },
  { value: "franchise_base", label: "Franchise en base — micro-entrepreneur (art. 293 B CGI)" },
  { value: "tva_standard", label: "TVA standard (20 / 10 / 5,5 %)" },
];

const STATUT_BADGE = {
  brouillon: "badge-muted",
  emise: "badge-valid",
  annulee: "badge-danger",
};

const STATUT_LABEL = {
  brouillon: "Brouillon",
  emise: "Émise",
  annulee: "Annulée",
};

function BillingCard({ user, onSaved }) {
  const [form, setForm] = useState({
    adresse: user.adresse || "",
    code_postal: user.code_postal || "",
    ville: user.ville || "",
    forme_juridique: user.forme_juridique || "",
    tva_intracom: user.tva_intracom || "",
    iban: user.iban || "",
    bic: user.bic || "",
    regime_tva_defaut: user.regime_tva_defaut || "autoliquidation",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/users/me/billing", form);
      toast.success("Coordonnées de facturation enregistrées");
      onSaved && onSaved();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="card p-4" data-testid="billing-form">
      <div className="flex items-center gap-2 mb-3">
        <Info size={16} className="text-[#1B3A5C]" />
        <h2 className="text-lg font-semibold text-[#1F2933]">Coordonnées de facturation</h2>
      </div>
      <p className="text-xs text-[#3E4C59] mb-4">
        Ces informations apparaissent sur les factures Factur-X (PDF/A-3 + XML EN 16931).
        L'adresse, le code postal et la ville sont obligatoires avant émission.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="label">Adresse</label>
          <input className="input" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} data-testid="billing-adresse" />
        </div>
        <div>
          <label className="label">Code postal</label>
          <input pattern="\d{5}" className="input" value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} data-testid="billing-cp" />
        </div>
        <div>
          <label className="label">Ville</label>
          <input className="input" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} data-testid="billing-ville" />
        </div>
        <div>
          <label className="label">Forme juridique</label>
          <input className="input" placeholder="SARL, SAS, EI, EURL…" value={form.forme_juridique} onChange={(e) => setForm({ ...form, forme_juridique: e.target.value })} data-testid="billing-forme" />
        </div>
        <div>
          <label className="label">N° TVA intracom.</label>
          <input className="input" placeholder="FR12345678901" value={form.tva_intracom} onChange={(e) => setForm({ ...form, tva_intracom: e.target.value })} data-testid="billing-tva" />
        </div>
        <div>
          <label className="label">IBAN</label>
          <input className="input" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="billing-iban" />
        </div>
        <div>
          <label className="label">BIC</label>
          <input className="input" value={form.bic} onChange={(e) => setForm({ ...form, bic: e.target.value })} data-testid="billing-bic" />
        </div>
        <div className="md:col-span-2">
          <label className="label">Régime TVA par défaut</label>
          <select className="select" value={form.regime_tva_defaut} onChange={(e) => setForm({ ...form, regime_tva_defaut: e.target.value })} data-testid="billing-regime">
            {REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>
      <button disabled={busy} className="btn-primary mt-4" data-testid="billing-save">
        {busy ? "…" : L.common.save}
      </button>
    </form>
  );
}

function NouvelleFactureForm({ missions, defaultRegime, onCreated }) {
  const [missionId, setMissionId] = useState("");
  const [regime, setRegime] = useState(defaultRegime || "autoliquidation");
  const [dateEch, setDateEch] = useState("");
  const [notes, setNotes] = useState("");
  const [lignes, setLignes] = useState([
    { designation: "", quantite: 1, unite: "forfait", prix_unitaire_ht: 0, taux_tva: 0 },
  ]);
  const [busy, setBusy] = useState(false);
  const [dest, setDest] = useState({ adresse: "", code_postal: "", ville: "" });
  const [needDest, setNeedDest] = useState(false);

  const mission = useMemo(() => missions.find((m) => m.id === missionId), [missionId, missions]);

  useEffect(() => {
    setRegime(defaultRegime || "autoliquidation");
  }, [defaultRegime]);

  useEffect(() => {
    if (mission) {
      setLignes([{ designation: mission.titre, quantite: 1, unite: "forfait", prix_unitaire_ht: mission.montant_ht || 0, taux_tva: mission.taux_tva || 0 }]);
    }
  }, [mission]);

  const totals = useMemo(() => {
    const ht = lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire_ht || 0), 0);
    const tva = regime === "tva_standard"
      ? lignes.reduce((s, l) => s + Number(l.quantite || 0) * Number(l.prix_unitaire_ht || 0) * (Number(l.taux_tva || 0) / 100), 0)
      : 0;
    return { ht, tva, ttc: ht + tva };
  }, [lignes, regime]);

  const setLigne = (i, patch) => setLignes(lignes.map((l, k) => k === i ? { ...l, ...patch } : l));
  const addLigne = () => setLignes([...lignes, { designation: "", quantite: 1, unite: "forfait", prix_unitaire_ht: 0, taux_tva: 0 }]);
  const removeLigne = (i) => setLignes(lignes.length > 1 ? lignes.filter((_, k) => k !== i) : lignes);

  const submit = async (e) => {
    e.preventDefault();
    if (!missionId) return toast.error("Sélectionnez une mission");
    if (lignes.some((l) => !l.designation.trim())) return toast.error("Chaque ligne doit avoir une désignation");
    setBusy(true);
    try {
      const payload = {
        mission_id: missionId,
        regime_tva: regime,
        date_echeance: dateEch || null,
        notes: notes || null,
        lignes: lignes.map((l) => ({
          designation: l.designation,
          quantite: Number(l.quantite),
          unite: l.unite,
          prix_unitaire_ht: Number(l.prix_unitaire_ht),
          taux_tva: Number(l.taux_tva || 0),
        })),
      };
      if (needDest && (dest.adresse || dest.code_postal || dest.ville)) {
        payload.destinataire_override = dest;
      }
      await api.post("/factures", payload);
      toast.success("Brouillon de facture créé");
      setMissionId(""); setDateEch(""); setNotes("");
      setLignes([{ designation: "", quantite: 1, unite: "forfait", prix_unitaire_ht: 0, taux_tva: 0 }]);
      onCreated();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const missionsFacturables = missions.filter((m) => m.statut !== "annulee");
  const selectedMission = missionsFacturables.find((m) => m.id === missionId);
  const pvWarning = selectedMission && selectedMission.pv_statut !== "valide";

  return (
    <form onSubmit={submit} className="card p-4" data-testid="facture-new-form">
      <div className="flex items-center gap-2 mb-3">
        <Plus size={16} className="text-[#1B3A5C]" />
        <h2 className="text-lg font-semibold text-[#1F2933]">Nouvelle facture depuis une mission</h2>
      </div>
      {missionsFacturables.length === 0 && (
        <p className="text-sm text-[#3E4C59]">Aucune mission facturable pour le moment.</p>
      )}
      {missionsFacturables.length > 0 && (
        <>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="label">Mission</label>
              <select className="select" value={missionId} onChange={(e) => setMissionId(e.target.value)} required data-testid="facture-mission">
                <option value="">Sélectionner…</option>
                {missionsFacturables.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.titre} — {m.entreprise_nom} — {fmtEUR(m.montant_ht)} HT
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Régime TVA</label>
              <select className="select" value={regime} onChange={(e) => setRegime(e.target.value)} data-testid="facture-regime">
                {REGIMES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Date d'échéance</label>
              <input type="date" className="input" value={dateEch} onChange={(e) => setDateEch(e.target.value)} data-testid="facture-echeance" />
              <div className="text-xs text-[#3E4C59] mt-1">Par défaut : J+30</div>
            </div>
          </div>

          {pvWarning && (
            <div className="mt-3 border-l-4 border-[#E8710A] bg-[#FFF7ED] p-3 text-[13px] text-[#1F2933]" data-testid="facture-pv-warning">
              <strong>Information</strong> — cette mission n'a pas de PV de réception validé
              {selectedMission?.pv_statut === "en_attente" && " (PV en attente de validation)"}
              {selectedMission?.pv_statut === "refuse" && " (PV refusé — resoumission possible)"}
              {!selectedMission?.pv_statut && " (aucun PV soumis)"}.
              Le PV n'est pas obligatoire pour émettre la facture, mais il sécurise la réception des travaux et facilite le paiement.
            </div>
          )}

          <div className="mt-4">
            <label className="label">Lignes</label>
            <div className="overflow-x-auto">
              <table className="table-compact w-full">
                <thead>
                  <tr>
                    <th className="min-w-[220px]">Désignation</th>
                    <th>Qté</th>
                    <th>Unité</th>
                    <th>PU HT</th>
                    {regime === "tva_standard" && <th>TVA %</th>}
                    <th>Total HT</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <input className="input" value={l.designation} onChange={(e) => setLigne(i, { designation: e.target.value })} required data-testid={`ligne-designation-${i}`} />
                      </td>
                      <td><input type="number" step="0.01" min="0" className="input" value={l.quantite} onChange={(e) => setLigne(i, { quantite: e.target.value })} data-testid={`ligne-qte-${i}`} /></td>
                      <td>
                        <select className="select" value={l.unite} onChange={(e) => setLigne(i, { unite: e.target.value })} data-testid={`ligne-unite-${i}`}>
                          <option>forfait</option><option>heure</option><option>jour</option><option>m²</option><option>ml</option><option>u</option>
                        </select>
                      </td>
                      <td><input type="number" step="0.01" min="0" className="input" value={l.prix_unitaire_ht} onChange={(e) => setLigne(i, { prix_unitaire_ht: e.target.value })} data-testid={`ligne-pu-${i}`} /></td>
                      {regime === "tva_standard" && (
                        <td>
                          <select className="select" value={l.taux_tva} onChange={(e) => setLigne(i, { taux_tva: e.target.value })} data-testid={`ligne-tva-${i}`}>
                            <option value="20">20 %</option>
                            <option value="10">10 %</option>
                            <option value="5.5">5,5 %</option>
                            <option value="0">0 %</option>
                          </select>
                        </td>
                      )}
                      <td className="font-semibold text-right">{fmtEUR(Number(l.quantite || 0) * Number(l.prix_unitaire_ht || 0))}</td>
                      <td>
                        <button type="button" onClick={() => removeLigne(i)} className="btn-ghost p-1" aria-label="Retirer" data-testid={`ligne-remove-${i}`}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" onClick={addLigne} className="btn-outline mt-2" data-testid="facture-add-line">
              <Plus size={16} /> Ajouter une ligne
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="label">Notes (optionnel)</label>
              <textarea rows={3} className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="facture-notes" />
              <label className="mt-2 flex items-center gap-2 text-sm text-[#3E4C59]">
                <input type="checkbox" checked={needDest} onChange={(e) => setNeedDest(e.target.checked)} data-testid="facture-need-dest" />
                Renseigner manuellement l'adresse du destinataire
              </label>
              {needDest && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <input className="input col-span-3" placeholder="Adresse" value={dest.adresse} onChange={(e) => setDest({ ...dest, adresse: e.target.value })} data-testid="facture-dest-adresse" />
                  <input className="input" placeholder="CP" value={dest.code_postal} onChange={(e) => setDest({ ...dest, code_postal: e.target.value })} data-testid="facture-dest-cp" />
                  <input className="input col-span-2" placeholder="Ville" value={dest.ville} onChange={(e) => setDest({ ...dest, ville: e.target.value })} data-testid="facture-dest-ville" />
                </div>
              )}
            </div>
            <div className="card p-3 bg-[#F5F6F7]">
              <div className="flex justify-between text-sm py-1"><span>Total HT</span><span className="font-semibold">{fmtEUR(totals.ht)}</span></div>
              {regime === "tva_standard" && (
                <div className="flex justify-between text-sm py-1"><span>Total TVA</span><span className="font-semibold">{fmtEUR(totals.tva)}</span></div>
              )}
              <div className="flex justify-between py-1 border-t border-[#CBD5E1] mt-1 pt-2">
                <span className="font-semibold">Total TTC</span>
                <span className="font-bold text-[#1B3A5C]">{fmtEUR(totals.ttc)}</span>
              </div>
              {regime !== "tva_standard" && (
                <p className="text-xs text-[#3E4C59] mt-2">
                  {regime === "autoliquidation"
                    ? "Autoliquidation — TVA due par le preneur."
                    : "TVA non applicable, article 293 B du CGI."}
                </p>
              )}
            </div>
          </div>

          <button disabled={busy} className="btn-primary mt-4" data-testid="facture-submit">
            {busy ? "…" : "Créer le brouillon"}
          </button>
        </>
      )}
    </form>
  );
}

function NumerotationAckModal({ onAcknowledged }) {
  const [prefix, setPrefix] = useState("F");
  const [startingNumber, setStartingNumber] = useState(1);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!checked) return toast.error("Vous devez cocher la case de reconnaissance.");
    if (!/^[A-Za-z0-9\-_]+$/.test(prefix)) return toast.error("Préfixe invalide (A-Z, 0-9, - et _).");
    setBusy(true);
    try {
      await api.post("/factures/settings/acknowledge", {
        prefix: prefix.trim(),
        starting_number: Number(startingNumber),
        acknowledged: true,
      });
      toast.success("Paramétrage enregistré. Le module de facturation est actif.");
      onAcknowledged();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto" data-testid="numerotation-ack-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-2xl rounded-[6px] border border-[#E4E7EB] shadow-lg my-4">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E4E7EB] bg-[#FFF7ED]">
          <AlertTriangle size={20} className="text-[#E8710A]" />
          <h2 className="text-lg font-semibold text-[#1F2933]">Avant d'utiliser le module de facturation</h2>
        </div>

        <div className="p-4 space-y-4 text-sm text-[#1F2933]">
          <div className="border-l-4 border-[#E8710A] bg-[#FFF7ED] p-3 text-[13px] leading-relaxed">
            <p className="font-semibold text-[#1F2933] mb-1">Attention</p>
            <p>
              La numérotation de vos factures doit être <strong>unique et continue</strong> sur l'ensemble
              de votre activité. Si vous émettez déjà des factures depuis un autre logiciel, <strong>n'utilisez
              pas ce module</strong> — vous créeriez une seconde séquence non conforme à l'
              <strong>article 242 nonies A du CGI</strong>. Vérifiez avec votre expert-comptable avant d'émettre.
            </p>
          </div>

          <div>
            <p className="text-[13px] text-[#3E4C59] mb-3">
              Si vous démarrez votre activité ou souhaitez basculer intégralement votre facturation ici,
              paramétrez ci-dessous le <strong>préfixe</strong> et le <strong>numéro de départ</strong>.
              Ces valeurs seront <strong>figées après votre première facture émise</strong>.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="label">Préfixe</label>
                <input
                  className="input"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  maxLength={10}
                  placeholder="F"
                  data-testid="ack-prefix"
                />
                <div className="text-xs text-[#3E4C59] mt-1">Ex. F, FACT, 2026-.</div>
              </div>
              <div>
                <label className="label">Numéro de départ</label>
                <input
                  type="number"
                  min="1"
                  max="999999"
                  className="input"
                  value={startingNumber}
                  onChange={(e) => setStartingNumber(e.target.value)}
                  data-testid="ack-starting"
                />
                <div className="text-xs text-[#3E4C59] mt-1">Utilisez le dernier n° + 1 de votre ancien logiciel.</div>
              </div>
            </div>
            <div className="mt-3 text-xs text-[#3E4C59]">
              Aperçu du prochain numéro : <span className="font-mono font-semibold text-[#1B3A5C]" data-testid="ack-preview">
                {prefix || "F"}-{new Date().getFullYear()}-{String(Number(startingNumber) || 1).padStart(6, "0")}
              </span>
            </div>
          </div>

          <label className="flex items-start gap-2 text-[13px] cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1"
              data-testid="ack-checkbox"
            />
            <span>
              J'ai lu et compris l'avertissement ci-dessus. Je certifie que je n'émets pas actuellement
              de factures via un autre logiciel avec une autre séquence, ou que je bascule intégralement
              sur ce module en poursuivant ma numérotation.
            </span>
          </label>
        </div>

        <div className="px-4 py-3 border-t border-[#E4E7EB] bg-[#F5F6F7] flex justify-end">
          <button
            type="submit"
            disabled={!checked || busy}
            className="btn-primary"
            data-testid="ack-submit"
          >
            {busy ? "…" : "Valider et activer la facturation"}
          </button>
        </div>
      </form>
    </div>
  );
}

function NumerotationInfo({ settings }) {
  return (
    <div className="card p-4 flex items-start gap-3" data-testid="numerotation-info">
      <Lock size={18} className="text-[#1B3A5C] mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-[#1F2933]">Paramètres de numérotation</div>
        <div className="text-xs text-[#3E4C59] mt-1">
          Préfixe : <span className="font-mono">{settings.prefix}</span> · Prochain n° prévu ≈ <span className="font-mono">{settings.prefix}-{new Date().getFullYear()}-{String(settings.starting_number).padStart(6, "0")}</span>
          {settings.locked && (
            <span className="ml-2 text-[#3E4C59]">— figé (des factures ont déjà été émises)</span>
          )}
          {settings.acknowledged_at && (
            <span className="ml-2 text-[#3E4C59]">— validé le {fmtDate(settings.acknowledged_at)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Factures() {
  const { user, refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, m, s] = await Promise.all([
        api.get("/factures"),
        user.role === "prestataire" ? api.get("/missions") : Promise.resolve({ data: [] }),
        user.role === "prestataire" ? api.get("/factures/settings") : Promise.resolve({ data: null }),
      ]);
      setRows(f.data);
      setMissions(m.data);
      setSettings(s.data);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setLoading(false);
    }
  }, [user.role]);

  useEffect(() => { load(); }, [load]);

  const [sp] = useSearchParams();
  const focusId = sp.get("facture");
  const [cmdMap, setCmdMap] = useState({});
  const [modal, setModal] = useState(null); // {type:'edit'|'recap', f}
  useEffect(() => {
    if (user.role !== "prestataire" && user.role !== "entreprise") return;
    api.get("/commandes").then((r) => { const m = {}; (r.data || []).forEach((c) => { m[c.id] = c; }); setCmdMap(m); }).catch(() => {});
  }, [user.role, rows]);

  const supprimerBrouillon = async (id) => {
    if (!window.confirm("Supprimer ce brouillon ? Aucun numéro n'a été consommé.")) return;
    try { await api.delete(`/factures/${id}`); toast.success("Brouillon supprimé"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  const emettre = async (id) => {
    setModal(null);
    try {
      await api.post(`/factures/${id}/emettre`);
      toast.success("Facture émise et scellée");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.actions)) {
        toast.error(d.message, { description: d.actions.join("\n\n"), duration: 15000 });
      } else {
        toast.error(fmtError(err));
      }
    }
  };

  const download = (signedUrl, numero, format = "pdf") => {
    if (!signedUrl) return;
    const full = `${BACKEND_URL}${signedUrl}${format !== "pdf" ? `&format=${format}` : ""}`;
    const a = document.createElement("a");
    a.href = full;
    a.download = format === "pa" ? `${numero}-facturx-plateforme-agreee.zip` : `${numero}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div>
      {user.role === "prestataire" && settings && !settings.acknowledged && (
        <NumerotationAckModal onAcknowledged={load} />
      )}

      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Factures Factur-X</h1>
        <p className="text-xs text-[#3E4C59]">Conformes à la norme EN 16931 (PDF/A-3 + XML CII embarqué).</p>
      </div>

      {user.role === "prestataire" && (
        <div className="mt-4 card p-4 border-l-4 border-[#E8710A] flex items-start gap-3" data-testid="pa-banner">
          <BookOpen size={18} className="text-[#E8710A] shrink-0 mt-0.5" />
          <div className="text-sm text-[#1F2933] leading-relaxed">
            <strong>Facturation électronique.</strong> Depuis le 1er septembre 2026, vous devez pouvoir recevoir des factures électroniques ; l'émission devient obligatoire pour les TPE au 1er septembre 2027 (15 € d'amende par facture non conforme).
            Vos factures sont déjà au format Factur-X : déposez-les sur votre plateforme agréée (anciennement PDP) avec le bouton <em>Pour ma plateforme agréée</em>.{" "}
            <Link to="/app/facturation-electronique" className="text-[#1B3A5C] underline font-semibold" data-testid="pa-guide-link">Comment faire →</Link>
          </div>
        </div>
      )}

      {user.role === "prestataire" && settings && settings.acknowledged && (
        <div className="mt-4">
          <NumerotationInfo settings={settings} />
        </div>
      )}

      {user.role !== "admin" && (
        <div className="mt-4">
          <BillingCard user={user} onSaved={() => refresh && refresh()} />
        </div>
      )}

      {user.role === "prestataire" && settings?.acknowledged && (
        <div className="mt-4">
          <NouvelleFactureForm
            missions={missions}
            defaultRegime={user.regime_tva_defaut || "autoliquidation"}
            onCreated={load}
          />
        </div>
      )}

      <div className="card mt-4 overflow-x-auto" data-testid="factures-list">
        <div className="p-3 border-b border-[#E4E7EB]">
          <h2 className="text-lg font-semibold text-[#1F2933]">
            {user.role === "entreprise" ? "Factures reçues" : user.role === "admin" ? "Toutes les factures" : "Mes factures"}
          </h2>
        </div>
        <table className="table-compact w-full">
          <thead>
            <tr>
              <th>N°</th>
              <th>{user.role === "entreprise" ? "Émetteur" : "Destinataire"}</th>
              <th>Date d'émission</th>
              <th>Échéance</th>
              <th>Régime</th>
              <th>Total HT</th>
              <th>Total TTC</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center text-[#3E4C59]">{L.common.loading}</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center text-[#3E4C59]">{L.common.none}</td></tr>
            )}
            {rows.map((f) => (
              <tr key={f.id} data-testid={`facture-row-${f.id}`} className={focusId === f.id ? "bg-[#FFF4EA]" : ""}>
                <td className="font-mono text-xs">{f.numero || <span className="text-[#3E4C59]">(brouillon)</span>}</td>
                <td>{user.role === "entreprise" ? f.prestataire_nom : f.entreprise_nom}</td>
                <td>{fmtDate(f.date_emission || f.created_at)}</td>
                <td>{fmtDate(f.date_echeance)}</td>
                <td className="text-xs">{f.regime_tva}</td>
                <td className="text-right">{fmtEUR(f.montant_ht)}</td>
                <td className="text-right font-semibold">{fmtEUR(f.montant_ttc)}</td>
                <td>
                  <span className={STATUT_BADGE[f.statut] || "badge-muted"} data-testid={`facture-statut-${f.id}`}>
                    {STATUT_LABEL[f.statut] || f.statut}
                  </span>
                </td>
                <td className="whitespace-nowrap">
                  {f.statut === "brouillon" && user.role === "prestataire" && (
                    <span className="inline-flex gap-1.5">
                      <button onClick={() => setModal({ type: "edit", f })} className="btn-outline" data-testid={`facture-edit-${f.id}`}>Modifier</button>
                      <button onClick={() => setModal({ type: "recap", f })} className="btn-accent" data-testid={`facture-emettre-${f.id}`}>
                        <Send size={14} /> Émettre
                      </button>
                      <button onClick={() => supprimerBrouillon(f.id)} className="btn-ghost text-[#C0392B]" title="Supprimer le brouillon" data-testid={`facture-delete-${f.id}`}><Trash2 size={14} /></button>
                    </span>
                  )}
                  {f.statut === "emise" && f.signed_url && (
                    <span className="inline-flex gap-1.5">
                      <button onClick={() => download(f.signed_url, f.numero)} className="btn-outline" data-testid={`facture-download-${f.id}`}>
                        <Download size={14} /> PDF
                      </button>
                      {user.role === "prestataire" && (
                        <button onClick={() => download(f.signed_url, f.numero, "pa")} className="btn-accent" title="Archive PDF + XML prête à déposer sur votre plateforme agréée (anciennement PDP)" data-testid={`facture-download-pa-${f.id}`}>
                          <FileArchive size={14} /> Pour ma plateforme agréée
                        </button>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {user.role === "prestataire" && <MesCommissions />}
      {modal?.type === "edit" && <FactureBrouillonModal facture={modal.f} commande={cmdMap[modal.f.commande_id]} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "recap" && <EmettreRecapModal facture={modal.f} commande={cmdMap[modal.f.commande_id]} onClose={() => setModal(null)} onConfirm={() => emettre(modal.f.id)} />}
    </div>
  );
}
